import { useAuthStore } from '../stores/auth-store';
import { env } from './env';

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

const isApiError = (e: unknown): e is ApiError =>
  typeof e === 'object' && e !== null && 'status' in e && 'body' in e;

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Fetch wrapper: attaches the current access token, handles 401 by attempting a
 * single refresh (deduped across concurrent callers), and JSON-parses the body.
 *
 * The refresh dance is intentionally minimal — one shared promise so a burst of
 * failed requests doesn't fan out into a burst of refresh calls.
 */
export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  { retryOn401 = true }: { retryOn401?: boolean } = {},
): Promise<T> {
  const url = `${env.apiBaseUrl}${path}`;
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { ...init, headers, credentials: 'include' });

  if (res.status === 401 && retryOn401) {
    const newToken = await refreshAccessToken();
    if (newToken) return apiFetch<T>(path, init, { retryOn401: false });
    useAuthStore.getState().clear();
  }

  const contentType = res.headers.get('content-type') ?? '';
  const body = contentType.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const err = new Error(
      typeof body === 'object' && body && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`,
    ) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${env.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const tokens = (await res.json()) as {
        accessToken: string;
        refreshToken: string;
        accessTokenExpiresAt: string;
      };
      useAuthStore.getState().setTokens(tokens);
      return tokens.accessToken;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Fetch a binary resource (PDFs, exports) with auth headers attached, returning
 * a Blob. Handles the same 401→refresh dance as apiFetch, but skips JSON parsing.
 */
export async function apiFetchBlob(
  path: string,
  init: RequestInit = {},
  { retryOn401 = true }: { retryOn401?: boolean } = {},
): Promise<Blob> {
  const url = `${env.apiBaseUrl}${path}`;
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { ...init, headers, credentials: 'include' });

  if (res.status === 401 && retryOn401) {
    const newToken = await refreshAccessToken();
    if (newToken) return apiFetchBlob(path, init, { retryOn401: false });
    useAuthStore.getState().clear();
  }
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as ApiError;
    err.status = res.status;
    err.body = await res.text().catch(() => '');
    throw err;
  }
  return res.blob();
}

export { isApiError };
